/**
 * Avatar rendering (ENGAGEMENT_PLAN §5.1 F1). Four shapes, one component:
 * an uploaded photo, a letter-styled seal from the shared catalog, a sample
 * face (bundled avataaars SVG), or the deterministic coloured-initial circle
 * when none is set. Seals and faces render client-side, so they cost no storage.
 */

import { useState } from 'react';
import { isFaceId, sealById } from '@toiyp/shared';
import type { AvatarView } from './api';
import { FaceImage } from './faces';

/** The deterministic palette lives in the token system (styles.css .avatar--i0..7). */
function avatarInitialClass(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return `avatar--i${Math.abs(h) % 8}`;
}

export function Avatar({
  name,
  avatar,
  label,
}: {
  name: string;
  avatar: AvatarView | null;
  /** Accessible label override — defaults to no label (avatars are decorative). */
  label?: string;
}) {
  // An upload whose bytes are gone (or can't load) falls back to the initial
  // circle rather than the browser's broken-image icon. Keyed by URL so a new
  // upload gets a fresh try.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (avatar?.kind === 'upload' && failedUrl !== avatar.url) {
    return (
      <img
        className="avatar avatar--img"
        src={avatar.url}
        alt={label ?? ''}
        loading="lazy"
        onError={() => setFailedUrl(avatar.url)}
      />
    );
  }
  if (avatar?.kind === 'seal') {
    const seal = sealById(avatar.url);
    if (seal) {
      return (
        <span className="avatar avatar--seal" role="img" aria-label={label ?? `${name}'s seal`}>
          {seal.glyph}
        </span>
      );
    }
  }
  if (avatar?.kind === 'face') {
    const id = isFaceId(avatar.url) ? avatar.url : null;
    if (id) {
      return (
        <span className="avatar avatar--face" role="img" aria-label={label ?? `${name}'s face`}>
          <FaceImage id={id} />
        </span>
      );
    }
  }
  return (
    <span
      className={`avatar avatar--initial ${avatarInitialClass(name)}`}
      aria-hidden="true"
    >
      {name[0]?.toUpperCase() ?? '?'}
    </span>
  );
}